/* eslint-disable max-params */
/* eslint-disable no-underscore-dangle */
import React from 'react';

import {
  LexicalEditor,
  EditorConfig,
  DecoratorNode,
  NodeKey,
  DOMConversionMap,
} from 'lexical';

import { MentionComponent } from '../../plugins';

import { $createMentionNode, $convertElement } from './MentionNode.helpers';

export type SerializedMentionNode = {
  type: string;
  version: number;
  mentionId: string;
  mentionName: string;
  mentionLogo?: string;
  mentionType: string;
};

export class MentionNode extends DecoratorNode<JSX.Element> {
  __removeHandler: () => void;

  __mentionId: string;

  __mentionName: string;

  __mentionLogo?: string;

  __mentionType: string;

  constructor(
    removeHandler: () => void,
    mentionId?: string,
    mentionName?: string,
    mentionLogo?: string,
    mentionType?: string,
    key?: NodeKey,
  ) {
    super(key);
    this.__removeHandler = removeHandler;
    this.__mentionId = mentionId || '';
    this.__mentionName = mentionName || '';
    this.__mentionLogo = mentionLogo;
    this.__mentionType = mentionType || '';
  }

  createDOM(_config: EditorConfig, editor: LexicalEditor): HTMLElement {
    const span = document.createElement('span');
    span.setAttribute('contenteditable', String(editor._editable));

    return span;
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(
      node.__removeHandler,
      node.__mentionId,
      node.__mentionName,
      node.__mentionLogo,
      node.__mentionType,
      node.__key,
    );
  }

  getMentionId(): string | undefined {
    return this.__mentionId;
  }

  static getType(): string {
    return 'mention-node';
  }

  updateNode = ({
    name,
    id,
    logo,
    type,
  }: {
    name: string;
    logo?: string;
    id: string;
    type: string;
  }) => {
    const self = this.getWritable();
    self.__mentionId = id;
    self.__mentionName = name;
    self.__mentionLogo = logo;
    self.__mentionType = type;
  };

  static importDOM(): DOMConversionMap | null {
    return {
      div: () => ({
        conversion: $convertElement,
        priority: 0,
      }),
    };
  }

  decorate(editor: LexicalEditor): JSX.Element {
    return (
      <MentionComponent
        updateNode={this.updateNode}
        initialValues={{
          name: this.__mentionName,
          id: this.__mentionId,
          logo: this.__mentionLogo,
          type: this.__mentionType,
        }}
        disableLink={!editor._editable}
        removeHandler={this.__removeHandler}
      />
    );
  }

  getMentionData() {
    return { type: this.__mentionType, id: this.__mentionId };
  }

  updateDOM(): boolean {
    return false;
  }

  insertNewAfter(): MentionNode {
    const newElement = $createMentionNode(
      this.__mentionId || '',
      this.__mentionName || '',
      this.__mentionLogo,
      this.__mentionType,
    );

    return newElement;
  }

  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    return $createMentionNode(
      serializedNode.mentionId,
      serializedNode.mentionName,
      serializedNode.mentionLogo,
      serializedNode.mentionType,
    );
  }

  exportJSON(): SerializedMentionNode {
    return {
      ...super.exportJSON(),
      type: this.getType(),
      mentionId: this.__mentionId,
      mentionName: this.__mentionName,
      mentionLogo: this.__mentionLogo,
      mentionType: this.__mentionType,
      version: 1,
    };
  }
}
